import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import StudentSidebar from '../components/StudentSidebar';
import AssignedReportCard from '../components/AssignedReportCard';
import AssignedReportTableView from '../components/AssignedReportTableView';
import { SearchIcon, GridIcon, ListIcon, ArrowLeftIcon, LayoutDashboardIcon } from 'lucide-react';
// Helper function to calculate status based on progress_pct
const getStudentStatus = (assessment: {
  progress_pct: number;
  dateCompleted?: string;
}) => {
  if (assessment.dateCompleted) return 'Completed';
  if (assessment.progress_pct >= 20) return 'In progress';
  if (assessment.progress_pct > 0 && assessment.progress_pct < 20) return 'Dropped Off Early';
  return 'Not started';
};
export function StudentReports() {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [assignedSearchQuery, setAssignedSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'title'>('date-desc');
  const [archivedAssessments, setArchivedAssessments] = useState<number[]>([]);
  const [educatorBannerDismissed, setEducatorBannerDismissed] = useState(false);
  // SIMULATED AUTH CONTEXT
  // In production, this would come from your auth provider
  const currentUserRole: 'educator' | 'learner' = 'educator';
  const isEducator = currentUserRole === 'educator';
  // Listen for filter events from sidebar
  useEffect(() => {
    const handleFilterEvent = (event: CustomEvent) => {
      setSelectedFilter(event.detail);
    };
    window.addEventListener('setReportsFilter' as any, handleFilterEvent as any);
    return () => {
      window.removeEventListener('setReportsFilter' as any, handleFilterEvent as any);
    };
  }, []);
  const assignedAssessmentsRaw = [{
    id: 1,
    title: 'Math Fundamentals: Fractions',
    mode: 'Challenge',
    questions: 15,
    dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dateAssigned: '2024-01-20',
    dateStarted: '2024-01-21',
    lastInteractedWith: '2024-01-23',
    progress_pct: 45,
    participants: 24,
    icon: '🔢',
    creator: 'Ms. Johnson',
    assignedByTeacher: true
  }, {
    id: 2,
    title: 'Science: States of Matter',
    mode: 'Coach',
    questions: 12,
    dueDate: '2024-01-28',
    dateAssigned: '2024-01-22',
    progress_pct: 0,
    participants: 18,
    icon: '🧪',
    creator: 'Mr. Smith',
    assignedByTeacher: true
  }, {
    id: 3,
    title: 'English: Grammar Basics',
    mode: 'Challenge',
    questions: 20,
    dateAssigned: '2024-01-15',
    dateStarted: '2024-01-16',
    dateCompleted: '2024-01-18',
    lastInteractedWith: '2024-01-18',
    progress_pct: 100,
    participants: 32,
    icon: '📝',
    creator: 'Mrs. Davis',
    score: 92,
    assignedByTeacher: true
  }, {
    id: 4,
    title: 'History: World War II',
    mode: 'Challenge',
    questions: 18,
    dateAssigned: '2024-01-10',
    dateStarted: '2024-01-11',
    dateCompleted: '2024-01-12',
    lastInteractedWith: '2024-01-12',
    progress_pct: 100,
    participants: 156,
    icon: '🏛️',
    creator: 'System',
    score: 88,
    assignedByTeacher: false
  }, {
    id: 5,
    title: 'Biology: Cell Structure',
    mode: 'Coach',
    questions: 14,
    dateAssigned: '2024-01-18',
    dateStarted: '2024-01-19',
    lastInteractedWith: '2024-01-20',
    progress_pct: 35,
    participants: 203,
    icon: '🧬',
    creator: 'System',
    assignedByTeacher: false
  }, {
    id: 6,
    title: 'Geography: Continents Quiz',
    mode: 'Challenge',
    questions: 10,
    dateAssigned: '2024-01-25',
    dateStarted: '2024-01-25',
    lastInteractedWith: '2024-01-25',
    progress_pct: 8,
    participants: 89,
    icon: '🌍',
    creator: 'Mr. Anderson',
    assignedByTeacher: true
  }, {
    id: 7,
    title: 'Physics: Motion Basics',
    mode: 'Coach',
    questions: 16,
    dateAssigned: '2024-01-23',
    dateStarted: '2024-01-24',
    lastInteractedWith: '2024-01-24',
    progress_pct: 12,
    participants: 67,
    icon: '⚡',
    creator: 'Dr. Chen',
    assignedByTeacher: true
  }];
  // Calculate status dynamically
  const assignedAssessments = assignedAssessmentsRaw.map(assessment => ({
    ...assessment,
    status: getStudentStatus(assessment)
  }));
  const handleArchiveToggle = (assessmentId: number) => {
    setArchivedAssessments(prev => prev.includes(assessmentId) ? prev.filter(id => id !== assessmentId) : [...prev, assessmentId]);
  };
  const filteredAssignedAssessments = assignedAssessments.filter(assessment => {
    const isArchived = archivedAssessments.includes(assessment.id);
    if (selectedFilter === 'archived') {
      return isArchived;
    } else if (selectedFilter !== 'all') {
      if (isArchived) return false;
    } else {
      if (isArchived) return false;
    }
    const matchesFilter = selectedFilter === 'all' || selectedFilter === 'archived' || selectedFilter === 'in-progress' && assessment.status === 'In progress' || selectedFilter === 'not-started' && assessment.status === 'Not started' || selectedFilter === 'completed' && assessment.status === 'Completed' || selectedFilter === 'dropped-off' && assessment.status === 'Dropped Off Early';
    const matchesSearch = assignedSearchQuery === '' || assessment.title.toLowerCase().includes(assignedSearchQuery.toLowerCase()) || assessment.creator?.toLowerCase().includes(assignedSearchQuery.toLowerCase()) || assessment.mode.toLowerCase().includes(assignedSearchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  }).sort((a, b) => {
    if (sortBy === 'date-desc') {
      return new Date(b.dateAssigned).getTime() - new Date(a.dateAssigned).getTime();
    } else if (sortBy === 'date-asc') {
      return new Date(a.dateAssigned).getTime() - new Date(b.dateAssigned).getTime();
    } else if (sortBy === 'title') {
      return a.title.localeCompare(b.title);
    }
    return 0;
  });
  // Count dropped-off assessments (excluding archived)
  const droppedOffCount = assignedAssessments.filter(a => a.status === 'Dropped Off Early' && !archivedAssessments.includes(a.id)).length;
  return <div className="flex min-h-screen bg-background" data-id="element-3760">
      <div className="hidden md:block" data-id="element-3761">
        <StudentSidebar droppedOffCount={droppedOffCount} data-id="element-3762" />
      </div>
      <div className="flex-1 flex flex-col min-w-0 w-full" data-id="element-3763">
        <Header data-id="element-3764" />
        <main className="flex-1 overflow-y-auto" data-id="element-3765">
          <div className="max-w-6xl mx-auto px-6 py-8" data-id="element-3766">
            {/* Educator Navigation Banner */}
            {isEducator && <motion.div initial={{
            opacity: 0,
            y: -12
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-6 bg-surface-elevated rounded-xl p-4 shadow-sm border border-[#2563EB]/20 border-l-4 border-l-[#2563EB]" data-id="element-3767">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-3768">
                  <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-3769">
                    <div className="w-9 h-9 rounded-lg bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0" data-id="element-3770">
                      <LayoutDashboardIcon size={18} className="text-[#2563EB]" data-id="element-3771" />
                    </div>
                    <div className="min-w-0" data-id="element-3772">
                      <p className="text-sm font-semibold text-text-primary" data-id="element-3773">
                        You're viewing the learner experience
                      </p>
                      <p className="text-xs text-text-secondary mt-0.5" data-id="element-3774">
                        This is what your students see. Switch back to manage
                        assessments and analytics.
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-12 sm:ml-0" data-id="element-3775">
                    <button onClick={() => navigate('/workspace')} className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-sm font-semibold hover:bg-[#1d4ed8] transition-colors shadow-sm" data-id="element-3776">
                      <ArrowLeftIcon size={15} data-id="element-3777" />
                      Educator Dashboard
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Assigned Assessments Section */}
            <section data-id="element-3778">
              <div className="flex items-center justify-between mb-6" data-id="element-3779">
                <h2 className="text-2xl font-bold text-text-primary" data-id="element-3780">
                  Assigned Assessments
                </h2>
              </div>
              {/* Controls: Filters and Actions */}
              <div className="space-y-4 mb-6" data-id="element-3781">
                {/* Filter Chips Row */}
                <div className="flex items-center gap-2 flex-wrap" data-id="element-3782">
                  {[{
                  id: 'all',
                  label: 'All'
                }, {
                  id: 'in-progress',
                  label: 'In progress'
                }, {
                  id: 'not-started',
                  label: 'Not started'
                }, {
                  id: 'dropped-off',
                  label: 'Dropped Off Early'
                }, {
                  id: 'completed',
                  label: 'Completed'
                }, {
                  id: 'archived',
                  label: 'Archived'
                }].map(filter => <button key={filter.id} onClick={() => setSelectedFilter(filter.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedFilter === filter.id ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F] border-2 border-[#00B75F] border-opacity-30' : 'bg-surface border-2 border-custom text-text-secondary hover:bg-surface-elevated'}`} data-id="element-3783">
                      {filter.label}
                      {filter.id === 'archived' && archivedAssessments.length > 0 && <span className="ml-1.5 text-xs" data-id="element-3784">
                            ({archivedAssessments.length})
                          </span>}
                      {filter.id === 'dropped-off' && droppedOffCount > 0 && <span className="ml-1.5 text-xs" data-id="element-3785">
                          ({droppedOffCount})
                        </span>}
                    </button>)}
                </div>
                {/* Search, Sort, and View Controls Row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-3786">
                  {/* Sort Dropdown */}
                  <select value={sortBy} onChange={e => setSortBy(e.target.value as 'date-desc' | 'date-asc' | 'title')} className="px-3 py-2 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary w-full sm:w-auto" data-id="element-3787">
                    <option value="date-desc" data-id="element-3788">Most Recent</option>
                    <option value="date-asc" data-id="element-3789">Oldest First</option>
                    <option value="title" data-id="element-3790">Title A-Z</option>
                  </select>
                  <div className="flex items-center gap-3" data-id="element-3791">
                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-surface border border-custom rounded-lg p-1" data-id="element-3792">
                      <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} aria-label="Grid view" data-id="element-3793">
                        <GridIcon size={18} data-id="element-3794" />
                      </button>
                      <button onClick={() => setViewMode('table')} className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} aria-label="Table view" data-id="element-3795">
                        <ListIcon size={18} data-id="element-3796" />
                      </button>
                    </div>
                    {/* Search Bar */}
                    <div className="relative flex-1 sm:w-64" data-id="element-3797">
                      <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary" data-id="element-3798" />
                      <input type="text" placeholder="Search assessments..." value={assignedSearchQuery} onChange={e => setAssignedSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-surface border border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent text-text-primary placeholder-text-tertiary" data-id="element-3799" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Results Count */}
              <div className="mb-4" data-id="element-3800">
                <p className="text-sm text-text-secondary" data-id="element-3801">
                  Showing {filteredAssignedAssessments.length} of{' '}
                  {assignedAssessments.length} assessments
                  {selectedFilter === 'archived' && archivedAssessments.length > 0 && ` (${archivedAssessments.length} archived)`}
                </p>
              </div>
              {/* Assigned Assessments Grid or Table */}
              {filteredAssignedAssessments.length === 0 ? <div className="bg-surface-elevated border border-custom rounded-xl p-8 text-center" data-id="element-3802">
                  <h3 className="text-lg font-semibold text-text-primary mb-2" data-id="element-3803">
                    {selectedFilter === 'archived' ? 'No archived assessments' : 'No assigned assessments found'}
                  </h3>
                  <p className="text-text-secondary text-sm" data-id="element-3804">
                    {selectedFilter === 'archived' ? 'Archive completed assessments to keep your workspace organized.' : 'Try adjusting your filters or search query.'}
                  </p>
                </div> : viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3805">
                  {filteredAssignedAssessments.map(assessment => <AssignedReportCard key={assessment.id} report={assessment} isArchived={archivedAssessments.includes(assessment.id)} onArchiveToggle={() => handleArchiveToggle(assessment.id)} data-id="element-3806" />)}
                </div> : <AssignedReportTableView reports={filteredAssignedAssessments} archivedReports={archivedAssessments} onArchiveToggle={handleArchiveToggle} data-id="element-3807" />}
            </section>
          </div>
        </main>
      </div>
    </div>;
}